// Universal Saturation Law (USL) Calculations
// Based on FRAI(dim, D) = D·dim / (D·dim + 1)

import { KalshiMarket, USLAnalysis, USL_PARAMS } from './types';

/**
 * Calculate USL prediction for market certainty based on volume
 * 
 * The USL predicts how "certain" a market should be given its liquidity.
 * Higher volume → more observers → higher expected agreement/certainty
 * 
 * @param volume - Trading volume
 * @param D - Drift parameter (default 0.83 from backtesting)
 * @returns Expected certainty (0-1)
 */
export function calculateUSLPrediction(volume: number, D: number = USL_PARAMS.D): number {
  const logVol = Math.log10(Math.max(volume, 1) + 1);
  return (D * logVol) / (D * logVol + 1);
}

/**
 * Calculate market certainty from price
 * Certainty = how far from 50/50 the market is
 * 
 * @param price - Market price (0-100)
 * @returns Certainty (0-1)
 */
export function calculateCertainty(price: number): number {
  return Math.abs(price - 50) / 50;
}

/**
 * Calculate USL residual (actual certainty - predicted certainty)
 * 
 * Positive residual → Market is MORE certain than USL predicts (overconfident)
 * Negative residual → Market is LESS certain than USL predicts (underconfident)
 * 
 * @param market - Kalshi market data
 * @returns USL residual
 */
export function calculateUSLResidual(market: KalshiMarket): number {
  const certainty = calculateCertainty(market.last_price);
  const prediction = calculateUSLPrediction(market.volume);
  return certainty - prediction;
}

/**
 * Full USL analysis for a market
 */
export function analyzeMarket(market: KalshiMarket): USLAnalysis {
  const logVolume = Math.log10(Math.max(market.volume, 1) + 1);
  const certainty = calculateCertainty(market.last_price);
  const uslPrediction = calculateUSLPrediction(market.volume);
  const uslResidual = certainty - uslPrediction;
  
  // Determine signal based on residual
  let signal: USLAnalysis['signal'];
  if (uslResidual > USL_PARAMS.SELL_THRESHOLD) {
    signal = 'SELL_CERTAINTY'; // Market overconfident
  } else if (uslResidual < USL_PARAMS.BUY_THRESHOLD) {
    signal = 'BUY_CERTAINTY';  // Market underconfident
  } else {
    signal = 'NEUTRAL';
  }
  
  return {
    logVolume,
    certainty,
    uslPrediction,
    uslResidual,
    signal,
  };
}

/**
 * Calculate USAD-style anomaly score using robust statistics
 * Uses median and MAD (Median Absolute Deviation) for robustness
 */
export function calculateAnomalyScore(
  residual: number,
  allResiduals: number[]
): number {
  if (allResiduals.length < 3) return 0;
  
  // Calculate median
  const sorted = [...allResiduals].sort((a, b) => a - b);
  const median = sorted[Math.floor(sorted.length / 2)];
  
  // Calculate MAD (Median Absolute Deviation)
  const deviations = sorted.map(r => Math.abs(r - median));
  const mad = deviations.sort((a, b) => a - b)[Math.floor(deviations.length / 2)];
  
  // Robust z-score (scaled MAD)
  const scaledMAD = mad * 1.4826; // Scale factor for normal distribution
  if (scaledMAD === 0) return 0;
  
  return Math.abs(residual - median) / scaledMAD;
}

/**
 * Format USL analysis for display
 */
export function formatUSLAnalysis(analysis: USLAnalysis): string {
  return `
    Log Volume: ${analysis.logVolume.toFixed(2)}
    Certainty: ${(analysis.certainty * 100).toFixed(1)}%
    USL Prediction: ${(analysis.uslPrediction * 100).toFixed(1)}%
    Residual: ${analysis.uslResidual > 0 ? '+' : ''}${(analysis.uslResidual * 100).toFixed(1)}%
    Signal: ${analysis.signal}
  `.trim();
}

/**
 * Batch analyze all markets
 */
export function batchAnalyzeMarkets(markets: KalshiMarket[]): Map<string, USLAnalysis> {
  const results = new Map<string, USLAnalysis>();
  const allResiduals: number[] = [];
  
  // First pass: calculate residuals
  for (const market of markets) {
    const analysis = analyzeMarket(market);
    results.set(market.ticker, analysis);
    allResiduals.push(analysis.uslResidual);
  }
  
  // Second pass: add anomaly scores
  for (const [, analysis] of results) {
    analysis.anomalyScore = calculateAnomalyScore(analysis.uslResidual, allResiduals);
  }
  
  return results;
}
